"use client";

import { useEffect, useState } from "react";
import { TweetSkeleton } from "react-tweet";
import type { Tweet } from "react-tweet/api";
import { CustomEmbeddedTweetClient } from "./custom-embedded-tweet-client";
import { tweetsApiResponseSchema } from "./tweets-api-schema";

type DeferredTweetEmbedProps = {
  tweetId: string;
  className?: string;
};

/**
 * Client-side tweet embed that fetches on mount via /api/tweets.
 * Avoids fetching tweets during static page generation (SSG).
 */
export function DeferredTweetEmbed({
  tweetId,
  className,
}: DeferredTweetEmbedProps) {
  const [tweet, setTweet] = useState<Tweet | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchTweet() {
      try {
        const response = await fetch(
          `/api/tweets?ids=${encodeURIComponent(tweetId)}`
        );
        if (!response.ok || cancelled) {
          return;
        }

        const json: unknown = await response.json();
        const parsed = tweetsApiResponseSchema.safeParse(json);
        if (!parsed.success || cancelled) {
          return;
        }

        const entry = parsed.data.tweets[0];
        if (entry) {
          setTweet(entry.tweet);
        }
      } finally {
        if (!cancelled) {
          setLoaded(true);
        }
      }
    }

    fetchTweet();
    return () => {
      cancelled = true;
    };
  }, [tweetId]);

  return (
    <div className={className ?? "mt-4 flex justify-center"}>
      <div className="[&_.react-tweet-theme]:!bg-transparent w-full max-w-[500px]">
        {(() => {
          if (!loaded) {
            return <TweetSkeleton />;
          }
          if (tweet) {
            return <CustomEmbeddedTweetClient tweet={tweet} />;
          }
          return null;
        })()}
      </div>
    </div>
  );
}
